import { useQuery } from '@tanstack/react-query';

import { Hub, type HubTab } from '../components/Hub';
import { ContractCockpitSection } from '../documents/ContractCockpitSection';
import { AreaMaklerFilter, AreaMaklerFilterToggle } from '../ops/AreaMaklerFilter';
import { PhoneConfirmations } from '../ops/PhoneConfirmations';
import { useAreaMaklerParams } from '../ops/areaMaklerParams';
import { SyncFailures } from '../ops/SyncFailures';
import { UnassignedAppointments } from '../ops/UnassignedAppointments';
import { useProject, useSupportOnly } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { AIReviewPage } from './AIReviewPage';
import { FollowUpsPage } from './FollowUpsPage';
import { GeoReviewPage } from './GeoReviewPage';
import { SupportPage } from './SupportPage';

// Posteingang: alles, was im Projekt offen ist, an einer Stelle – Rückfragen, Rückrufe und Wiedervorlagen,
// Termine ohne Makler, unklare Adressen, KI-Prüfungen und Outlook-Fehler. Filterbar nach Gebiet und Makler;
// jeder Eintrag führt zum Lead oder Termin, wo die Aufgabe auch im Kontext steht.
export function InboxPage() {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const { areaId, maklerId } = useAreaMaklerParams();
  const supportOnly = useSupportOnly();
  const dash = useQuery({ queryKey: projectKeys.dashboard(project.id), queryFn: ({ signal }) => papi.dashboard(signal), refetchInterval: 60_000 });
  const s = dash.data?.support;
  // Telefonische Terminbestätigungen (OFFENE_FRAGEN B-29): Zähler aus der Liste – Überfällige rot.
  const confirmations = useQuery({
    queryKey: projectKeys.phoneConfirmations(project.id, 'open'),
    queryFn: ({ signal }) => papi.phoneConfirmations('open', signal),
    enabled: can('call.start') && !supportOnly,
    refetchInterval: 60_000,
  });
  const cockpit = useQuery({
    queryKey: projectKeys.contractCockpit(project.id),
    queryFn: ({ signal }) => papi.contractCockpit(signal),
    enabled: can('document.read'),
    refetchInterval: 30_000,
  });
  const pendingApprovalCount = (cockpit.data?.items ?? []).filter((i) => i.approval_status === 'PENDING_APPROVAL').length;
  const contractCount = cockpit.data?.items.length;

  const tabs: HubTab[] = [];
  if (can('ticket.manage'))
    tabs.push({
      key: 'rueckfragen',
      label: supportOnly ? 'Aufgaben' : 'Rückfragen',
      count: s?.open_tickets,
      tone: s?.urgent_tickets ? 'error' : 'warn',
      render: () => <SupportPage embedded focused={supportOnly} areaId={areaId} fieldSalesUserId={maklerId} />,
    });
  if (can('document.read'))
    tabs.push({
      key: 'vertraege',
      label: 'Verträge & Freigabe',
      count: pendingApprovalCount > 0 ? pendingApprovalCount : contractCount,
      tone: pendingApprovalCount > 0 ? 'error' : 'warn',
      render: () => <ContractCockpitSection />,
    });
  if (can('call.start') && !supportOnly)
    tabs.push({
      key: 'bestaetigungen',
      label: 'Terminbestätigungen',
      count: confirmations.data?.items.length,
      tone: confirmations.data?.items.some((i) => i.overdue) ? 'error' : 'warn',
      render: () => <PhoneConfirmations />,
    });
  if (can('task.read') && !supportOnly)
    tabs.push({
      key: 'wiedervorlagen',
      label: 'Rückrufe & Wiedervorlagen',
      count: s?.overdue_follow_ups,
      tone: 'warn',
      render: () => <FollowUpsPage embedded defaultAssigned="all" areaId={areaId} fieldSalesUserId={maklerId} />,
    });
  if (can('calendar.read') && !supportOnly)
    tabs.push({
      key: 'ohne-makler',
      label: 'Termine ohne Makler',
      count: s?.unassigned_appointments,
      tone: 'warn',
      render: () => <UnassignedAppointments areaId={areaId} />,
    });
  if (can('lead.geo_review'))
    tabs.push({ key: 'adressen', label: 'Adresse unklar', count: s?.geo_review_open, render: () => <GeoReviewPage embedded /> });
  if (can('ai.review'))
    tabs.push(
      supportOnly
        ? { key: 'ki', label: 'Kontaktdaten (KI)', count: s?.ai_contact_reviews_pending, render: () => <AIReviewPage embedded contactOnly /> }
        : { key: 'ki', label: 'KI-Prüfung', count: s?.ai_reviews_pending, render: () => <AIReviewPage embedded /> },
    );
  if (can('integration.outlook.manage') && !supportOnly)
    tabs.push({ key: 'outlook', label: 'Outlook-Fehler', count: s?.sync_failures, tone: 'error', render: () => <SyncFailures /> });

  if (supportOnly)
    return (
      <Hub
        title="Meine Aufgaben"
        description="Infos, Daten, Unterlagen und Verträge – nach Art und Dringlichkeit sortiert."
        tabs={tabs}
        keep={['gebiet', 'makler']}
        filters={<AreaMaklerFilterToggle />}
        label="Aufgabenarten"
      />
    );
  return <Hub title="Posteingang" description="Alles, was das Büro klären muss – Zähler zeigen, wo etwas offen ist." tabs={tabs} keep={['gebiet', 'makler']} filters={<AreaMaklerFilter />} label="Aufgabenarten" />;
}
